import { EM_DASH } from "@opensea/i18n"
import type { TextBodyProps } from "@opensea/ui-kit"
import {
  classNames,
  Flex,
  FlexColumn,
  ItemContent,
  ItemDescription,
  ItemSide,
  ItemTitle,
  NumberDisplay,
  Skeleton,
  TextBody,
} from "@opensea/ui-kit"
import type { BigNumberLike } from "@opensea/utils"
import { Item } from "../Item"

type OrdersSummaryBaseProps = {
  children: React.ReactNode
}

const OrdersSummary = ({ children }: OrdersSummaryBaseProps) => {
  return <FlexColumn className="my-4 gap-4">{children}</FlexColumn>
}

type OrdersSummaryItemProps = {
  children: React.ReactNode
  className?: string
}

const OrdersSummaryItem = ({ children, className }: OrdersSummaryItemProps) => {
  return (
    <Item className={classNames("gap-2", className)} variant="unstyled">
      {children}
    </Item>
  )
}

type OrdersSummaryTokenValueProps = {
  value: number
  usdValue: BigNumberLike | undefined
  symbol: string
  isFee?: boolean
  variant?: "value" | "result"
  overrides?: {
    Text?: TextBodyProps
  }
}

const OrdersSummaryTokenValue = ({
  value,
  usdValue,
  symbol,
  isFee = false,
  variant = "value",
  overrides,
}: OrdersSummaryTokenValueProps) => {
  return (
    <Flex
      className={classNames({
        "items-baseline gap-1.5": variant === "value",
        "flex-col-reverse items-end": variant === "result",
      })}
    >
      <TextBody
        color="text-secondary"
        size="xs"
        {...overrides?.Text}
        className={classNames(
          "text-nowrap font-mono",
          overrides?.Text?.className,
        )}
      >
        ({isFee && value !== 0 ? EM_DASH : null}
        <NumberDisplay display="usd" value={usdValue} />)
      </TextBody>
      <TextBody
        weight={variant === "result" ? "semibold" : undefined}
        {...overrides?.Text}
        className={classNames(
          "text-nowrap font-mono",
          overrides?.Text?.className,
        )}
      >
        {isFee && value !== 0 ? EM_DASH : null}
        <NumberDisplay suffix={symbol} value={value} />
      </TextBody>
    </Flex>
  )
}

const OrdersSummaryTokenValueSkeleton = ({
  variant = "value",
}: Pick<OrdersSummaryTokenValueProps, "variant">) => {
  return (
    <Flex
      className={classNames({
        "items-baseline gap-1.5": variant === "value",
        "flex-col-reverse items-end": variant === "result",
      })}
    >
      <Skeleton className="h-3 w-10" />
      <Skeleton className="h-3.5 w-14" />
    </Flex>
  )
}

type OrdersSummaryItemSideProps = React.ComponentProps<typeof ItemSide>

const OrdersSummaryItemSide = ({
  className,
  children,
  ...restProps
}: OrdersSummaryItemSideProps) => {
  return (
    <ItemSide {...restProps} className={classNames("max-w-none", className)}>
      {children}
    </ItemSide>
  )
}

export {
  OrdersSummary,
  OrdersSummaryItem,
  ItemContent as OrdersSummaryItemContent,
  ItemTitle as OrdersSummaryItemTitle,
  ItemDescription as OrdersSummaryItemDescription,
  OrdersSummaryItemSide,
  OrdersSummaryTokenValue,
  OrdersSummaryTokenValueSkeleton,
}
